import { styled } from '@mui/material/styles'
import { colors } from 'constants/theme'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import UseWindowSize from 'utils/useWindowSize'

/** COMPONENT */
import { getColorStatusAppointment, renderIconStatusAppointment } from 'component/Appointment/CardAppointment'
import CardAppointmentDetail from 'component/Appointment/CardAppointmentDetail'
import CalendarDay, { CalendarItem } from 'component/Calendar/CalendarMonth/CalendarMonth'
import { CustomCalenderMonth } from 'features/counter/appointment/CalendarStyle'
import { AppointmentCardItemDayInterface, AppointmentDisplayDayInterface, AppointmentDisplayWeekInterface } from 'features/counter/appointment/appointment-interface'

export interface IDisplayMonthProps {
  branchId: number
  branch?: string
  year: number
  month: number
  listAppointment: AppointmentDisplayWeekInterface[]
  onEdit?: (id: number) => void
  onEditSpecialInfo?: (id: number) => void
  onEditFeedback?: (id: number) => void
  onLoading?: (event: boolean) => void
}

const DivIcon = styled('div')(({ theme }) => ({
  '& img': {
    width: '17px',
    height: '17px',
    verticalAlign: 'top',
    marginTop: '0.25rem'
  }
}))

const DivCustomDetail = styled('div')(({ theme }) => ({
  '& .card': {
    border: `1px solid ${colors.lightGray}`,
    '&:hover': {
      boxShadow: `0px 3px 6px ${colors.black08}`
    },
    '& .div-section-icon': {
      width: '30px',
      [theme.breakpoints.down('lg')]: {
        width: '10px'
      }
    },
    '& .div-icon': {
      width: '17px',
      height: '17px',
      [theme.breakpoints.down('lg')]: {
        display: 'none'
      }
    }
  }
}))

interface DailyAppointmentsProps {
  doctors: AppointmentDisplayDayInterface[]
  viewDetailsId: number | null
  onViewDetail: (id: number) => void
  onCloseDetail: () => void
  onEdit?: (id: number) => void
  onEditSpecialInfo?: (id: number) => void
  onEditFeedback?: (id: number) => void
  onLoading?: (event: boolean) => void
  branch?: string
}

const DailyAppointments = ({
  doctors,
  viewDetailsId,
  onViewDetail,
  onCloseDetail,
  onEdit,
  onEditSpecialInfo,
  onEditFeedback,
  onLoading,
  branch,
}: DailyAppointmentsProps) => {
  const sortedAppointments = useMemo(() => {
    if (!doctors || doctors.length === 0) {
      return []
    }
    const allAppointments = doctors.flatMap(doctor => doctor.appointments || [])
    return _.sortBy(allAppointments, 'start')
  }, [doctors])

  if (sortedAppointments.length === 0) {
    return null
  }

  return (
    <div className="custom-scroll mt-1" style={{ overflow: 'auto', overflowX: 'hidden', height: '120px' }}>
      {sortedAppointments.map((ap: AppointmentCardItemDayInterface) => (
        <DivCustomDetail key={ap.appointmentId} className="-position-relative px-2">
          <Card style={{ cursor: 'pointer', marginBottom: '2px', backgroundColor: ap.color }} onClick={() => onViewDetail(ap.appointmentId)}>
            <div className="d-flex">
              <div className=" d-inline-block " style={{ backgroundColor: getColorStatusAppointment(ap.statusAppointment), borderRadius: '0.25rem 0 0 0.25rem' }}>
                <div className="d-flex justify-content-center div-section-icon" >
                  <DivIcon className="text-center div-icon" >
                    {renderIconStatusAppointment(ap.statusAppointment, true)}
                  </DivIcon>
                </div>
              </div>
              <div title={`${ap.rangeTimeText} | ${ap.patientFullname} | ${ap.serviceText}`} className="d-inline-block text-ellipsis pl-1" style={{ fontWeight: 400 }}>
                {ap.rangeTimeText} | {ap.patientFullname} | {ap.serviceText}
              </div>
            </div>
          </Card>
          {viewDetailsId === ap.appointmentId && (
            <CardAppointmentDetail
              displayType='month'
              fixPosition={{ top: `36px`, left: '0px' }}
              appointmentId={ap.appointmentId}
              showPopup={true}
              onClose={onCloseDetail}
              onEdit={onEdit}
              onEditSpecialInfo={onEditSpecialInfo}
              onEditFeedback={onEditFeedback}
              onLoading={onLoading}
              status={ap.statusAppointment}
              branch={branch}
            />
          )}
        </DivCustomDetail>
      ))}
    </div>
  )
}

export default function DisplayMonth(props: IDisplayMonthProps) {
  const [bodyHeight, setBodyHeight] = useState(Array.from(document.getElementsByClassName('container-content') as HTMLCollectionOf<HTMLElement>)[0]?.offsetHeight || 0);
  const headHeight = Array.from(document.getElementsByClassName('head-appointment') as HTMLCollectionOf<HTMLElement>)[0]?.offsetHeight || 0
  const [heightScreen] = UseWindowSize()
  const [viewDetailsId, setViewDetailsId] = useState<number | null>(null)

  useEffect(() => {
    setBodyHeight(Array.from(document.getElementsByClassName('container-content') as HTMLCollectionOf<HTMLElement>)[0]?.offsetHeight || 0)
  }, [heightScreen]);

  const handleViewDetail = (appointmentId: number) => {
    setViewDetailsId(appointmentId)
  }
  const handleCloseDetail = () => {
    setViewDetailsId(null)
  }

  return (
    <div className="position-relative">
      <Row className="mx-0">
        <Col className="d-none d-sm-block px-0 col-auto"></Col>
        <Col className="px-0 overflow-auto mt-3 custom-scroll" style={{ maxHeight: `calc(${bodyHeight - headHeight}px - 34px)` }}>
          <CustomCalenderMonth className='mx-3'>
            <CalendarDay
              yearAndMonth={[props.year, props.month]}
              renderDay={(calendarDayObject: any) => {
                const laInDay: any = props.listAppointment.find((la: AppointmentDisplayWeekInterface) => la.date === calendarDayObject.dateString)
                if (laInDay?.doctors.length) {
                  laInDay.date = calendarDayObject.dateString
                }
                return (
                  <CalendarItem
                    calendarDayObject={calendarDayObject}
                    allDentists={laInDay?.doctors || []}
                    items={
                      <DailyAppointments
                        doctors={laInDay?.doctors || []}
                        viewDetailsId={viewDetailsId}
                        onViewDetail={handleViewDetail}
                        onCloseDetail={handleCloseDetail}
                        onEdit={props.onEdit}
                        onEditSpecialInfo={props.onEditSpecialInfo}
                        onEditFeedback={props.onEditFeedback}
                        onLoading={props.onLoading}
                        branch={props.branch}
                      />
                    }
                  />
                )
              }}
            />
          </CustomCalenderMonth>
        </Col>
      </Row>
    </div>
  )
}